/**
 * Holds the File bytes behind picked analysis photos, OUTSIDE Redux — the same
 * split DraftMediaFileStore uses for product media, and for the same reason:
 * File isn't serializable, so state carries only an `id` that keys in here.
 *
 * The split earns something extra in this feature. Analysis state is persisted
 * so a curator can refresh or navigate away mid-flow (see ImageAnalyzeDb), and
 * everything Redux holds survives that. The Files do not. That is deliberate
 * rather than a gap:
 *
 *   - An image that has been analysed doesn't need its File any more. The
 *     backend already stored the original to Supabase and handed back a
 *     `publicUrl`, so the thumbnail keeps rendering from the network after a
 *     refresh and the draft already points at stored media.
 *   - An image still queued or failed DOES need its File to be (re)sent, and
 *     after a refresh it's gone. Those come back marked `orphaned` and the UI
 *     asks for them to be picked again, rather than offering a retry button
 *     that could only ever fail.
 *
 * Keyed by a uuid minted here, so ids never collide across setups and a single
 * module-level map serves every session on the page. Unlike DraftMediaFileStore
 * there is no bare `clear()` — two setup rows can hold analysis sessions at the
 * same time, and wiping one must not empty the other. Use `removeMany` with the
 * ids of the session being cleared.
 */

const files = new Map<string, File>();

/**
 * Object URLs for local previews, minted once per file at put() time.
 *
 * Cached rather than created per render because URL.createObjectURL leaks a
 * document-lifetime blob reference on every call — a tile re-rendering during a
 * batch run would otherwise mint one per frame with nothing revoking them.
 * Revoked in remove()/removeMany(), which is the only place a file leaves.
 */
const previewUrls = new Map<string, string>();

const AnalyzeImageFileStore = {
  /** Stores a file and returns its generated id — the id the UI, the batches
   *  and the thunks all key on from here on. */
  put(file: File): string {
    const id = crypto.randomUUID();
    files.set(id, file);
    previewUrls.set(id, URL.createObjectURL(file));
    return id;
  },

  get(id: string): File | undefined {
    return files.get(id);
  },

  /** True while the bytes are still in this tab. False after a refresh, which
   *  is what marks an unanalysed image as orphaned. */
  has(id: string): boolean {
    return files.has(id);
  },

  /** A local blob: URL for an image not yet uploaded, or undefined once the
   *  bytes are gone. Callers fall back to the server's publicUrl, which is the
   *  better source anyway the moment analysis succeeds. */
  previewUrl(id: string): string | undefined {
    return previewUrls.get(id);
  },

  /**
   * Every id currently held.
   *
   * Exists for the reconciler in ImageAnalyzePersistence, which compares this
   * against the ids the live sessions still reference and drops the difference.
   * That is what closes the one exit path the explicit remove() calls miss:
   * logout wipes the slice through `resetAllState`, and no component is mounted
   * at that moment to clean up after it.
   */
  ids(): string[] {
    return [...files.keys()];
  },

  remove(id: string): void {
    const url = previewUrls.get(id);
    if (url) URL.revokeObjectURL(url);
    previewUrls.delete(id);
    files.delete(id);
  },

  /** Bulk remove for clearing one session without touching another's ids. */
  removeMany(ids: readonly string[]): void {
    ids.forEach((id) => AnalyzeImageFileStore.remove(id));
  },
};

export default AnalyzeImageFileStore;
